import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { GenericErrorApiAdapter } from '../../../adapters/GenericErrorApiAdapter';
import { LabelRecordFailedErrorApiAdapter } from '../../../adapters/LabelRecordFailedErrorApiAdapter';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';

export const GENERIC_ERROR_I18N_PREFIX = 'genericError' as const;
export const LABEL_RECORD_FAILED_I18N_PREFIX = 'labelRecordFailedError' as const;

export type GenericErrorModalI18nPrefix = typeof GENERIC_ERROR_I18N_PREFIX | typeof LABEL_RECORD_FAILED_I18N_PREFIX;

export interface GenericErrorModalOpenOptions {
  translationKeyPrefix?: GenericErrorModalI18nPrefix;
}

export interface GenericErrorModalRef {
  openModal: (orderId: number, errorMessage?: string, options?: GenericErrorModalOpenOptions) => void;
  closeModal: () => void;
}

interface GenericErrorModalProps {
  onSuccess?: () => void;
}

export const GenericErrorModal = forwardRef<GenericErrorModalRef, GenericErrorModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);
  const [courierErrorMessage, setCourierErrorMessage] = useState<string | null>(null);
  const [i18nKeyPrefix, setI18nKeyPrefix] = useState<GenericErrorModalI18nPrefix>(GENERIC_ERROR_I18N_PREFIX);

  useImperativeHandle(ref, () => ({
    openModal: (orderId: number, errorMessage?: string, options?: GenericErrorModalOpenOptions) => {
      setCurrentOrderId(orderId);
      setCourierErrorMessage(errorMessage || null);
      setI18nKeyPrefix(options?.translationKeyPrefix ?? GENERIC_ERROR_I18N_PREFIX);
      modalRef.current?.openModal();
    },
    closeModal: () => modalRef.current?.closeModal(),
  }));

  const handleClose = () => {
    modalRef.current?.closeModal();
  };

  const handleBack = () => {
    modalRef.current?.closeModal();
  };

  const handleRetry = async () => {
    if (!currentOrderId) {
      toast({
        title: t(`${i18nKeyPrefix}.error.title`),
        description: 'ID del pedido no encontrado',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const response =
        i18nKeyPrefix === LABEL_RECORD_FAILED_I18N_PREFIX
          ? await new LabelRecordFailedErrorApiAdapter(httpClient).solveLabelRecordFailedError(currentOrderId)
          : await new GenericErrorApiAdapter(httpClient).solveGenericError(currentOrderId);

      if (response.success) {
        toast({
          title: t(`${i18nKeyPrefix}.success.title`),
          description: response.message,
          variant: 'default',
        });

        // Ejecutar callback de éxito si existe
        onSuccess?.();
      } else {
        toast({
          title: t(`${i18nKeyPrefix}.error.title`),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      // Traducir el error usando el sistema de traducción
      const translatedError = translateOrderApiError(error, t);

      toast({
        title: t(`${i18nKeyPrefix}.error.title`),
        description: translatedError,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t(`${i18nKeyPrefix}.title`)}
      icon={
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
        </button>
      }
      type="default"
      fullScreenMobile={true}
      width="lg"
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-full">
        {/* Contenido principal */}
        <div className="flex-1 space-y-4 mt-2 pb-24 md:pb-0 max-h-[70vh] overflow-y-auto">
          <p className="text-sm">{t(`${i18nKeyPrefix}.description`)}</p>

          {/* Alert box con error específico */}
          <div className="border border-error dark:border-[#4C0707] bg-bg-error dark:bg-[#280B13] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 h-8">
                <div className="w-8 h-8 rounded-full bg-error-buttons flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-[#BC7679] light:!text-btn-text w-4 h-4"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="text-error text-sm font-medium">
                  {t(`${i18nKeyPrefix}.errorMessage`)}
                  {courierErrorMessage && <span className="block mt-1 uppercase">{courierErrorMessage}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción - Fijos en mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} size="lg">
              {t(`${i18nKeyPrefix}.cancel`)}
            </Button>
            <Button variant="primary" onClick={handleRetry} size="lg" disabled={isSubmitting}>
              {isSubmitting ? t(`${i18nKeyPrefix}.submitting`) : t(`${i18nKeyPrefix}.retry`)}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});
